import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, Spinner } from '@/components/v2/ui'
import {
  useCancelListingRemake,
  useListingAuditReport,
  useListingRemakeFull,
  useListingRemakeSlots,
  useListingRemakeStatus,
  useListingRemakeVersions,
} from '@/hooks/useListingBuilder'
import { EID } from '@/lib/element-ids.registry'

import { AdoptRemakeButton } from './AdoptRemakeButton'
import { RemakeImageSlots } from './RemakeImageSlots'
import { RemakeTextDiff } from './RemakeTextDiff'
import { RemakeVersionSelect } from './RemakeVersionSelect'

const PENDING = ['pending', 'running', 'generating']

interface RemakeResultProps {
  jobId: string
}

/** Reads remake status and renders progress / completed diff+slots / error. */
export function RemakeResult({ jobId }: RemakeResultProps) {
  const { t } = useTranslation()
  const { data: status } = useListingRemakeStatus(jobId)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Default selection to the latest remake_id whenever it changes (adjust
  // state while rendering — avoids an effect+setState round-trip). The
  // previous remake_id is tracked in state per the React docs pattern.
  const [seenRemakeId, setSeenRemakeId] = useState<string | null | undefined>(undefined)
  if (status?.remake_id && status.remake_id !== seenRemakeId) {
    setSeenRemakeId(status.remake_id)
    setSelectedId(status.remake_id)
  }

  if (!status || status.status === 'none') return null

  if (PENDING.includes(status.status)) {
    return (
      <div className="listing-builder-remake-progress flex items-center gap-3" aria-live="polite">
        <Spinner size="sm" />
        <span>{t('listingBuilder.remake.inProgress')}</span>
        {status.remake_id && <CancelButton jobId={jobId} remakeId={status.remake_id} />}
      </div>
    )
  }

  if (status.status === 'failed') {
    return (
      <Alert variant="error" className="listing-builder-remake-error">
        {status.error_message ?? t('listingBuilder.remake.failed')}
      </Alert>
    )
  }

  return <RemakeCompleted jobId={jobId} remakeId={selectedId} onSelect={setSelectedId} />
}

function CancelButton({ jobId, remakeId }: { jobId: string; remakeId: string }) {
  const { t } = useTranslation()
  const cancel = useCancelListingRemake(jobId)
  return (
    <Button
      id={EID['listing-builder-remake-cancel-btn']}
      type="button"
      variant="outline"
      disabled={cancel.isPending}
      onClick={() => cancel.mutate(remakeId)}
    >
      {t('listingBuilder.remake.cancelButton')}
    </Button>
  )
}

function RemakeCompleted({
  jobId,
  remakeId,
  onSelect,
}: {
  jobId: string
  remakeId: string | null
  onSelect: (id: string) => void
}) {
  const { data: full } = useListingRemakeFull(remakeId)
  const { data: slotsData } = useListingRemakeSlots(remakeId)
  const { data: versionsData } = useListingRemakeVersions(jobId)
  const { data: report } = useListingAuditReport(jobId)
  const before = (report?.source_input as Record<string, unknown> | null | undefined) ?? null

  return (
    <div className="listing-builder-remake-completed space-y-6">
      <RemakeVersionSelect
        versions={versionsData?.versions ?? []}
        value={remakeId}
        onChange={onSelect}
      />
      <RemakeTextDiff
        before={before}
        after={full?.text_result ?? null}
        fields={full?.fields ?? undefined}
      />
      {remakeId && full?.sales_channel_id && <AdoptRemakeButton remakeId={remakeId} />}
      <RemakeImageSlots slots={slotsData?.slots ?? []} />
    </div>
  )
}
